import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GlowBackdrop, GlassCard, GradientButton, GhostButton } from "@/components/primitives";

function strength(pw) {
  let s = 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ name: "", email: "", password: "", company: "", role: "engineer" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ps = useMemo(() => strength(data.password), [data.password]);
  const psColors = ["bg-red-500", "bg-orange-500", "bg-yellow-400", "bg-cyan-400", "bg-emerald-400"];

  const next = () => setStep((s) => Math.min(s + 1, 2));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await register(data);
      nav("/app/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not register");
    } finally { setBusy(false); }
  };

  const canNext = (step === 0 && data.name && data.email) || (step === 1 && data.password.length >= 6) || step === 2;

  return (
    <div className="min-h-screen relative grid place-items-center px-6 py-14">
      <GlowBackdrop intense />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-500 grid place-items-center"><Sparkles className="h-4 w-4 text-white" /></div>
          <div className="text-sm font-bold tracking-tight">SentinelAI</div>
        </Link>
        <GlassCard className="p-8">
          <div className="flex items-center gap-3 mb-5">
            {[0,1,2].map((i) => (
              <div key={i} className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: step >= i ? "100%" : "0%" }} className="h-full bg-gradient-to-r from-cyan-400 to-violet-500" />
              </div>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              {step === 0 && (
                <>
                  <h1 className="text-2xl font-bold">Create your workspace</h1>
                  <p className="text-sm text-slate-400 mt-1">Tell us a bit about yourself.</p>
                  <div className="mt-6 space-y-4">
                    <Field label="Full name" testid="register-name" value={data.name} onChange={(v) => setData({ ...data, name: v })} />
                    <Field label="Work email" type="email" testid="register-email" value={data.email} onChange={(v) => setData({ ...data, email: v })} />
                  </div>
                </>
              )}
              {step === 1 && (
                <>
                  <h1 className="text-2xl font-bold">Secure your account</h1>
                  <p className="text-sm text-slate-400 mt-1">Choose a strong password (6+ chars).</p>
                  <div className="mt-6 space-y-4">
                    <Field label="Password" type="password" testid="register-password" value={data.password} onChange={(v) => setData({ ...data, password: v })} />
                    <div className="flex gap-1.5">
                      {[0,1,2,3].map((i) => (
                        <div key={i} className={`flex-1 h-1.5 rounded-full ${i < ps ? psColors[ps] : "bg-white/5"}`} />
                      ))}
                    </div>
                    <div className="text-xs text-slate-400">{["Too weak","Weak","Okay","Strong","Excellent"][ps]}</div>
                  </div>
                </>
              )}
              {step === 2 && (
                <>
                  <h1 className="text-2xl font-bold">Tell us about your team</h1>
                  <p className="text-sm text-slate-400 mt-1">We&apos;ll tailor SentinelAI to your workflow.</p>
                  <div className="mt-6 space-y-4">
                    <Field label="Company" testid="register-company" value={data.company} onChange={(v) => setData({ ...data, company: v })} />
                    <label className="block">
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Your role</div>
                      <select data-testid="register-role" value={data.role} onChange={(e) => setData({ ...data, role: e.target.value })} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm">
                        {["engineer","sre","security","manager","admin"].map((r) => <option key={r} value={r} className="bg-slate-900">{r.toUpperCase()}</option>)}
                      </select>
                    </label>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {err && <div className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="register-error">{err}</div>}

          <div className="mt-6 flex items-center justify-between">
            {step > 0 ? <GhostButton onClick={back} data-testid="register-back"><ArrowLeft className="h-4 w-4" /> Back</GhostButton> : <span />}
            {step < 2
              ? <GradientButton onClick={next} disabled={!canNext} data-testid="register-next">Continue <ArrowRight className="h-4 w-4" /></GradientButton>
              : <GradientButton onClick={submit} disabled={busy} data-testid="register-submit">{busy ? "Creating…" : (<>Create account <CheckCircle2 className="h-4 w-4" /></>)}</GradientButton>
            }
          </div>

          <div className="mt-6 text-center text-sm text-slate-400">
            Already have one? <Link to="/login" className="text-cyan-300">Sign in</Link>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      <input
        data-testid={testid}
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.04] border border-white/10 focus:border-cyan-400/50 focus:outline-none rounded-xl px-4 py-2.5 text-sm placeholder:text-slate-500"
      />
    </label>
  );
}
